import React, { useState } from "react";
import {Modal, Form, Button, Schema, Message} from "rsuite";
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
    faCalendarAlt, 
    faBed, 
    faUsers, 
    faEuroSign, 
    faPhone, 
    faEnvelope, 
    faUser,
    faClock,
    faCheckCircle,
    faInfoCircle
} from "@fortawesome/free-solid-svg-icons";

import axios from 'axios';
import { differenceInDays } from 'date-fns'

const { StringType } = Schema.Types;

const BookingModal = ({ showModal, setShowModal, selectedRoom, checkinDate, checkoutDate, selectedRoomData, person, nbrooms, setSuccessMessage }) => {
    const { t } = useTranslation();
    const [formValue, setFormValue] = useState({
        firstName: "",
        lastName: "",
        phoneNumber: "",
        email: "",
    });

    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [isAvailable] = useState(true);
    const form = React.useRef();
    const [nuits, setNuits] = useState(0)
    const [total, setTotal] = useState(0)
    const [data, setData] = useState([])
    
    const model = React.useMemo(() => Schema.Model({
        firstName: StringType().isRequired(t("modal.validation.firstNameRequired")),
        lastName: StringType().isRequired(t("modal.validation.lastNameRequired")),
        phoneNumber: StringType()
            .pattern(
                /^(\+\d{1,3})?[-.\s]?(\(\d{1,3}\))?[-.\s]?(\d{1,4})[-.\s]?(\d{1,4})[-.\s]?(\d{1,9})$/,
                t("modal.validation.phoneInvalid")
            )
            .isRequired(t("modal.validation.phoneRequired")),
        email: StringType()
            .isEmail(t("modal.validation.emailInvalid"))
            .isRequired(t("modal.validation.emailRequired")),
    }), [t]);
     const handleSubmit = async () => {
        if (!form.current.check()) {
            return;
        }

        setLoading(true);

        // Perform booking
        axios.post('https://backend.hotel-romilly.fr/api/booking', { ...formValue, selectedRoom, checkinDate, checkoutDate, person, nbrooms, nuits })
            .then(response => {
                console.log('Booking successful:', response.data);
                setSuccessMessage(t("modal.messages.success"))
                // Handle any success actions here, e.g., showing a success message
            })
            setShowModal(false)
            .catch(error => {
                setErrorMessage(t("modal.messages.error"));
                // Handle any error actions here, e.g., showing an error message
            });

        setLoading(false);


    };
    const onOpen = () => {
        setSuccessMessage("");
        setErrorMessage("");
        
        // Vérification de la validité des données avant calcul
        if (!checkinDate || !checkoutDate || !selectedRoomData || !selectedRoom) {
            setErrorMessage("Données de réservation incomplètes");
            return;
        }
        
        let nbnuits = differenceInDays(checkoutDate, checkinDate);
        
        if (nbnuits <= 0) {
            setErrorMessage("Les dates sélectionnées ne sont pas valides");
            return;
        }
        
        setNuits(nbnuits);
        setTotal(nbnuits * selectedRoomData.rate * nbrooms);
        setData([{
            room: selectedRoom,
            rate: selectedRoomData.rate + t("modal.table.perNight"),
            nbrooms: nbrooms,
            person: person,
            nuits: nbnuits,
        }]);
    };
    return (
        <Modal 
            size={"lg"} 
            open={showModal} 
            onOpen={()=> onOpen()} 
            onClose={() => {setShowModal(false); setSuccessMessage(""); setErrorMessage("")}}
            className="elegant-booking-modal"
        >
            <Modal.Header>
                <Modal.Title className="booking-modal-title">
                    <FontAwesomeIcon icon={faCalendarAlt} className="title-icon" />
                    {t('modal.title')}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="booking-modal-body">
                {/* Booking Summary Card */}
                <div className="booking-summary-card">
                    <h4 className="summary-title">
                        <FontAwesomeIcon icon={faCheckCircle} className="summary-icon" />
                        {t('modal.totalTitle')}
                    </h4>
                    
                    <div className="booking-details-grid">
                        <div className="detail-item">
                            <div className="detail-icon">
                                <FontAwesomeIcon icon={faBed} />
                            </div>
                            <div className="detail-content">
                                <span className="detail-label">{t('modal.table.room')}</span>
                                <span className="detail-value">{selectedRoom}</span>
                            </div>
                        </div>
                        
                        <div className="detail-item">
                            <div className="detail-icon">
                                <FontAwesomeIcon icon={faCalendarAlt} />
                            </div>
                            <div className="detail-content">
                                <span className="detail-label">{t('modal.table.nbNights')}</span>
                                <span className="detail-value">{nuits} {nuits > 1 ? 'nuits' : 'nuit'}</span>
                            </div>
                        </div>
                        
                        <div className="detail-item">
                            <div className="detail-icon">
                                <FontAwesomeIcon icon={faUsers} />
                            </div>
                            <div className="detail-content">
                                <span className="detail-label">{t('modal.table.nbPersons')}</span>
                                <span className="detail-value">{person}</span>
                            </div>
                        </div>
                        
                        <div className="detail-item">
                            <div className="detail-icon">
                                <FontAwesomeIcon icon={faBed} />
                            </div>
                            <div className="detail-content">
                                <span className="detail-label">{t('modal.table.nbRooms')}</span>
                                <span className="detail-value">{nbrooms}</span>
                            </div>
                        </div>
                    </div>
                    
                    <div className="price-breakdown">
                        <div className="price-line">
                            <span>{selectedRoomData.rate}€ × {nuits} {nuits > 1 ? 'nuits' : 'nuit'} × {nbrooms} {nbrooms > 1 ? 'chambres' : 'chambre'}</span>
                            <span>{selectedRoomData.rate * nuits * nbrooms}€</span>
                        </div>
                        <div className="total-price">
                            <FontAwesomeIcon icon={faEuroSign} className="euro-icon" />
                            <span className="total-label">{t('modal.table.total')}</span>
                            <span className="total-amount">{total}€</span>
                        </div>
                    </div>
                    
                    <div className="tax-info">
                        <FontAwesomeIcon icon={faInfoCircle} className="info-icon" />
                        <span>{t('modal.taxInfo')}</span>
                    </div>
                </div>

                {/* Payment Information */}
                <div className="payment-info-card">
                    <FontAwesomeIcon icon={faClock} className="payment-icon" />
                    <div className="payment-text">
                        <p>{t('modal.paymentInfo')}</p>
                        <p><strong>{t('modal.paymentMethod')}</strong></p>
                    </div>
                </div>

                {/* Contact Form */}
                <div className="contact-form-card">
                    <h4 className="form-title">
                        <FontAwesomeIcon icon={faUser} className="form-icon" />
                        {t('modal.contactTitle')}
                    </h4>
                    
                    { errorMessage !== "" &&
                    <Message type="error" showIcon className="error-message">
                        { errorMessage }
                    </Message>
                    }

                    <Form
                        fluid
                        model={model}
                        formValue={formValue}
                        onChange={setFormValue}
                        ref={form}
                        className="elegant-form"
                    >
                        <div className="form-row">
                            <Form.Group className="form-group-half">
                                <Form.ControlLabel className="elegant-label">
                                    <FontAwesomeIcon icon={faUser} className="label-icon" />
                                    {t('modal.form.firstName')}
                                </Form.ControlLabel>
                                <Form.Control name="firstName" className="elegant-input" />
                            </Form.Group>
                            <Form.Group className="form-group-half">
                                <Form.ControlLabel className="elegant-label">
                                    <FontAwesomeIcon icon={faUser} className="label-icon" />
                                    {t('modal.form.lastName')}
                                </Form.ControlLabel>
                                <Form.Control name="lastName" className="elegant-input" />
                            </Form.Group>
                        </div>
                        <div className="form-row">
                            <Form.Group className="form-group-half">
                                <Form.ControlLabel className="elegant-label">
                                    <FontAwesomeIcon icon={faPhone} className="label-icon" />
                                    {t('modal.form.phone')}
                                </Form.ControlLabel>
                                <Form.Control name="phoneNumber" className="elegant-input" />
                            </Form.Group>
                            <Form.Group className="form-group-half">
                                <Form.ControlLabel className="elegant-label">
                                    <FontAwesomeIcon icon={faEnvelope} className="label-icon" />
                                    {t('modal.form.email')}
                                </Form.ControlLabel>
                                <Form.Control name="email" className="elegant-input" />
                            </Form.Group>
                        </div>
                    </Form>
                </div>
            </Modal.Body>
            <Modal.Footer className="booking-modal-footer">
                <Button 
                    onClick={handleSubmit} 
                    className="reserve-button" 
                    loading={loading} 
                    disabled={!isAvailable}
                    size="lg"
                >
                    <FontAwesomeIcon icon={faCheckCircle} className="button-icon" />
                    {t('modal.buttons.reserve')}
                </Button>
                <Button 
                    appearance="subtle" 
                    onClick={() => setShowModal(false)}
                    className="cancel-button"
                    size="lg"
                >
                    {t('modal.buttons.cancel')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default BookingModal;
